import { useMemo } from 'react'
import { ChevronLeft } from 'lucide-react'
import { Header } from '@components/Header.tsx'
import { useRouter } from '@router'
import type { Activity } from '@hewey/shared'
import { ActivityCard } from './ActivityCard.tsx'
import styles from './AllActivitiesPage.module.css'

type Props = {
	activities: Activity[]
	loading: boolean
}

// The full list, A–Z by name, as a dense single column of compact cards.
// Reached from the Explore overview's "View all" button; the header's back
// button returns there.
export const AllActivitiesPage = ({ activities, loading }: Props) => {
	const { navigate } = useRouter()

	const sorted = useMemo(
		() => [...activities].sort((a, b) => a.name.localeCompare(b.name)),
		[activities],
	)

	const backButton = (
		<button
			type="button"
			className={styles.back}
			onClick={() => navigate('/activities')}
			aria-label="Back to overview"
		>
			<ChevronLeft size={22} aria-hidden="true" />
		</button>
	)

	return (
		<main className={styles.screen}>
			<Header
				left={backButton}
				center={<h1 className={styles.title}>All activities</h1>}
			/>

			{loading && activities.length === 0 ? (
				<p className={styles.empty}>Loading…</p>
			) : sorted.length === 0 ? (
				<p className={styles.empty}>Nothing yet.</p>
			) : (
				<ul className={styles.list}>
					{sorted.map((a) => (
						<li key={a.id}>
							<ActivityCard activity={a} compact />
						</li>
					))}
				</ul>
			)}
		</main>
	)
}
